import LoadingDotsBrand from '@/components/animations/LoadingDotsBrand/LoadingDotsBrand';
import LoadingDotsDestructiveBold from '@/components/animations/LoadingDotsDestructiveBold/LoadingDotsDestructiveBold';
import LoadingDotsLink from '@/components/animations/LoadingDotsLink/LoadingDotsLink';
import LoadingDotsSecondary from '@/components/animations/LoadingDotsSecondary/LoadingDotsSecondary';
import LoadingDotsWhite from '@/components/animations/LoadingDotsWhite/LoadingDotsWhite';
import { classnames } from '@/helpers';
import { customTwMerge, themed } from '@/helpers/theme';
import { ButtonHTMLAttributes, HTMLAttributes, LegacyRef, forwardRef } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { ButtonSize, ButtonVariant } from '../types';
import css from './Button.module.scss';

export const BUTTON_PADDING = {
  xs: 'p-xs',
  sm: 'p-sm',
  md: 'p-sm',
  lg: 'px-lg py-md',
} as const;

const BASE_STYLE = {
  xs: 'text-m rounded-xs gap-x-2xs',
  sm: 'text-m rounded-xs gap-x-xs',
  md: 'text-l rounded-xs gap-x-xs',
  lg: 'text-xl rounded-sm gap-x-sm',
};

const base = (size: ButtonSize) => classnames('inline-flex items-center justify-center', `${BASE_STYLE[size]}`);

const getVariant = (variant: ButtonVariant, disabled: boolean) => {
  const variants = {
    primary: themed(
      `${
        disabled
          ? 'bg-bg_secondary_bold text-fg_disabled'
          : 'text-fg_primary_inverse bg-bg_primary hover:bg-bg_primary_hover'
      }`,
      `${disabled ? '' : 'hover:text-black-800'} text-black-800 bg-sm_primary`,
    ),
    secondary: classnames(
      'bg-bg_secondary_bold',
      disabled
        ? themed('text-fg_disabled', '!text-sm_primary !border-sm_primary-300')
        : themed('text-fg_primary hover:bg-bg_secondary_hover', 'hover:bg-sm_primary hover:text-black-800'),
    ),
    tertiary: themed(
      `${
        disabled ? 'bg-bg_secondary_bold text-fg_disabled' : 'bg-bg_brand text-fg_alwaysLight hover:bg-bg_brand_hover'
      }`,
      'text-black-800',
    ),
    destructiveBold: themed(
      `${
        disabled
          ? 'bg-bg_secondary_bold text-fg_disabled'
          : 'bg-bg_negative_bold text-fg_alwaysLight hover:bg-bg_negative_hover'
      }`,
      'text-black-800',
    ),
    link: `${disabled ? 'text-fg_disabled' : 'text-fg_link hover:text-fg_link_hover underline'}`,
    lastMinute: `${disabled ? 'bg-bg_secondary_bold text-fg_disabled' : 'text-fg_primary bg-bg_notice_bold'}`,
  };

  return variants[variant];
};

const btnClasses = (
  variant: ButtonVariant,
  size: ButtonSize,
  block: boolean,
  disabled: boolean,
  className?: string,
  inline?: boolean,
) =>
  customTwMerge(
    base(size),
    getVariant(variant, disabled),
    block && '!flex !w-full sm:!w-full',
    inline ? '!outline-none' : BUTTON_PADDING[size],
    className,
  );

type Icon = JSX.Element;

type GenericButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  label?: string;
  leftIcon?: Icon;
  rightIcon?: Icon;
  iconNoFilter?: boolean;
  disabled?: boolean;
  loading?: boolean;
  inline?: boolean;
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & GenericButtonProps;

const IconWrapper = ({ icon, size }: { icon: Icon; size: ButtonSize }) => {
  return <span className={classnames(css.icon, css[size])}>{icon}</span>;
};

const getLoadingIcon = (variant: ButtonVariant, size: ButtonSize) => {
  const loadingIconStyle = {
    xs: { top: '-5px', width: '48px', height: '19px' },
    sm: { top: '-5px', width: '48px', height: '19px' },
    md: { top: '-3px', width: '48px', height: '22px' },
    lg: { top: '-2px', width: '48px', height: '24px' },
  };
  const loadingIcons = {
    primary: <LoadingDotsWhite loop style={loadingIconStyle[size]} />,
    secondary: <LoadingDotsSecondary loop style={loadingIconStyle[size]} />,
    tertiary: <LoadingDotsBrand loop style={loadingIconStyle[size]} />,
    destructiveBold: <LoadingDotsDestructiveBold loop style={loadingIconStyle[size]} />,
    link: <LoadingDotsLink loop style={loadingIconStyle[size]} />,
  };

  return loadingIcons[variant];
};

const Button = forwardRef(
  (
    {
      variant = 'primary',
      label,
      leftIcon,
      rightIcon,
      size = 'sm',
      block,
      className = '',
      children,
      loading,
      inline,
      iconNoFilter = false,
      ...props
    }: ButtonProps,
    ref: LegacyRef<HTMLButtonElement>,
  ) => {
    return (
      <button
        ref={ref}
        className={`${css.button} ${css[variant]} ${css[size]} ${
          iconNoFilter ? '' : css['invertIconColor']
        } ${btnClasses(variant, size, block, props.disabled, className, inline)}`}
        {...props}>
        {leftIcon && <IconWrapper size={size} icon={leftIcon} />}
        {loading ? getLoadingIcon(variant, size) : label ?? children}
        {rightIcon && <IconWrapper size={size} icon={rightIcon} />}
      </button>
    );
  },
);

/**
 * In cases where a button is only needed for styling purposes but does not
 * work semantically to use a real button.
 */
type FakeButtonProps = HTMLAttributes<HTMLDivElement> & GenericButtonProps;

export const FakeButton = ({
  variant = 'primary',
  label,
  leftIcon,
  rightIcon,
  size = 'sm',
  block,
  className = '',
  loading,
  inline,
  iconNoFilter = false,
  ...props
}: FakeButtonProps) => {
  return (
    <div
      className={`${css.button} ${css[variant]} ${css[size]} ${iconNoFilter ? '' : css['invertIconColor']} ${btnClasses(
        variant,
        size,
        block,
        props.disabled,
        className,
        inline,
      )}`}
      {...props}>
      {leftIcon && <IconWrapper size={size} icon={leftIcon} />}
      {loading ? getLoadingIcon(variant, size) : label}
      {rightIcon && <IconWrapper size={size} icon={rightIcon} />}
    </div>
  );
};

export const LinkButton = ({
  variant = 'link',
  label,
  leftIcon,
  rightIcon,
  size = 'sm',
  block,
  className = '',
  children,
  loading,
  iconNoFilter = false,
  ...props
}: ButtonProps & LinkProps) =>
  (props.to && typeof props.to === 'string' && props.to.startsWith('/')) || props.to?.pathname ? (
    <Link
      className={`${css.button} ${css[variant]} ${css[size]} ${iconNoFilter ? '' : css['invertIconColor']} ${btnClasses(
        variant,
        size,
        block,
        props.disabled,
        className,
      )}`}
      {...props}>
      {leftIcon && <IconWrapper size={size} icon={leftIcon} />}
      {loading ? getLoadingIcon(variant, size) : label ?? children}
      {rightIcon && <IconWrapper size={size} icon={rightIcon} />}
    </Link>
  ) : (
    <a
      href={props.to ?? '#'}
      {...props}
      className={`${css.button} ${css[variant]} ${css[size]} ${btnClasses(
        variant,
        size,
        block,
        props.disabled,
        className,
      )}`}
      {...props}>
      {leftIcon && <IconWrapper size={size} icon={leftIcon} />}
      {loading ? getLoadingIcon(variant, size) : label ?? children}
      {rightIcon && <IconWrapper size={size} icon={rightIcon} />}
    </a>
  );

export default Button;
